/** Less than comparison with a tolerance for small floating point mistakes. */
export function floatLt(
  a: number,
  b: number,
  precision = 0.0000000001,
): boolean {
  return b - a > precision;
}

/** Greater than comparison with a tolerance for small floating point mistakes. */
export function floatGt(
  a: number,
  b: number,
  precision = 0.0000000001,
): boolean {
  return a - b > precision;
}

/** Equality comparison with a tolerance for small floating point mistakes. */
export function floatEq(
  a: number,
  b: number,
  precision = 0.0000000001,
): boolean {
  return Math.abs(a - b) < precision;
}

export function floatCmp(
  a: number,
  b: number,
  precision = 0.0000000001,
): number {
  if (Math.abs(a - b) < precision) return 0;
  return a - b > 0 ? 1 : -1;
}

export type MathValue = number | MathExpr;

export type MathExpr = {
  precedence: number;
  toString(): string;
  valueOf(): number;
};

const parens = (current: MathValue, child: MathValue): string =>
  (typeof current === 'number' ? Infinity : current.precedence) >=
  (typeof child === 'number' ? Infinity : child.precedence)
    ? `(${child})`
    : child.toString();

/** Value which can also display the formula */
export const mathVar = (value: number, name?: string): MathExpr => ({
  precedence: Infinity,
  toString() {
    return name ?? value.toString();
  },
  valueOf() {
    return value;
  },
});

const binaryOp =
  (
    operator: string,
    precedence: number,
    valueFn: (a: MathValue, b: MathValue) => number,
  ) =>
  (a: MathValue, b: MathValue, name?: string): MathExpr => ({
    precedence: precedence,
    toString() {
      return name ?? `${parens(this, a)} ${operator} ${parens(this, b)}`;
    },
    valueOf() {
      return valueFn(a, b);
    },
  });

/** Computation which can also display the formula */
export const mathMul = binaryOp('*', 12, (a, b) => +a * +b);
/** Computation which can also display the formula */
export const mathDiv = binaryOp('/', 12, (a, b) => +a / +b);
/** Computation which can also display the formula */
export const mathAdd = binaryOp('+', 11, (a, b) => +a + +b);
/** Computation which can also display the formula */
export const mathSub = binaryOp('-', 11, (a, b) => +a - +b);
/** Computation which can also display the formula */
export const mathSum = (...args: MathValue[]): MathExpr => ({
  precedence: 17,
  toString() {
    return `sum(${args[0]})`;
  },
  valueOf() {
    return args.reduce<number>((acc, b) => acc + +b, 0);
  },
});
/** Computation which can also display the formula */
export const mathMin = (...args: MathValue[]): MathExpr => ({
  precedence: 17,
  toString() {
    return `min(${args[0]})`;
  },
  valueOf() {
    return args.reduce<number>((acc, b) => Math.min(acc, +b), Infinity);
  },
});
/** Computation which can also display the formula */
export const mathMax = (...args: MathValue[]): MathExpr => ({
  precedence: 17,
  toString() {
    return `max(${args[0]})`;
  },
  valueOf() {
    return args.reduce<number>((acc, b) => Math.max(acc, +b), -Infinity);
  },
});

/** Examples:
const x = mathVar(17, 'x');
const y = mathVar(27, 'y');
const r = mathMul(x, y);
const r2 = mathMul(x, y, 'r2'); // Replacing the display of "x * y" with "r2"

console.log(x, y, r, r2);
console.log(+x, +y, +r, +r2); // Get the numerical value of each expression
console.log(x.toString(), y.toString(), r.toString(), r2.toString()); // Print the string representation / formula of each expression
console.log(mathSum(x, x, x).toString());
console.log(mathDiv(x, mathAdd(mathDiv(y, 13), 3)).toString()); // A more complex example, will use some parens: "x / (y / 13 + 3)"
*/

/**
 * Performs linear interpolation of a given value on a line segment.
 * Given some `x: number` and a set of two points on a plane.
 *
 * @param lineSegment a single pair of points, where each point is a pair of `[x, y]` coordinates
 * @returns an estimated interpolated value
 * @throws {Error} If all points have the same x-coordinate (vertical line)
 *
 * @example
 * ```typescript
 * const points = [[1, 2], [3, 6]];
 * const value = 2
 * lerp(value, points) // 4
 * ```
 */
export const simpleLerp = <T extends [number, number]>(
  value: number,
  lineSegment: [T, T],
): number => {
  const [x1, y1] = lineSegment[0];
  const [x2, y2] = lineSegment[1];

  if (x1 === x2) throw new Error('Cannot interpolate over a vertical line');

  const k = (y2 - y1) / (x2 - x1);
  const b = y1 - k * x1;

  return value * k + b;
};
